import { useState, useEffect, useCallback, useRef } from 'react';

function useExcelData(initialData = []) {
  const [data, setData] = useState(initialData); // 모든 row data

  const originalIds = useRef(new Set()); // 초기에 로드된 행 관리
  const [addedRows, setAddedRows] = useState([]); // 새로 추가된 행 관리

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setData(initialData);
      originalIds.current = new Set(initialData.map(item => item.id));
      setAddedRows([]);
    }
  }, [initialData]);

  const resetData = useCallback(newData => {
    if (newData && newData.length > 0) {
      setData(newData);
      originalIds.current = new Set(newData.map(item => item.id));
      setAddedRows([]);
    } else {
      setData([]);
      originalIds.current = new Set();
      setAddedRows([]);
    }
  }, []);

  const handleCellsChanged = useCallback(changes => {
    setData(prevData => {
      const newData = [...prevData];

      changes.forEach(change => {
        const { rowId, columnId, newCell, previousCell } = change;
        const rowIndex = newData.findIndex(
          item => String(item.id) === String(rowId),
        );

        if (rowIndex === -1) return;

        if (newCell.type === 'checkbox') {
          newData[rowIndex] = {
            ...newData[rowIndex],
            [columnId]: newCell.checked,
          };
          return;
        }

        if (newCell.type === 'dropdown') {
          if (newCell.isOpen !== previousCell.isOpen) {
            newData[rowIndex] = {
              ...newData[rowIndex],
              [`${columnId}_isOpen`]: newCell.isOpen,
            };
          }

          if (
            newCell.selectedValue !== previousCell.selectedValue &&
            newCell.selectedValue
          ) {
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: newCell.selectedValue,
            };
          }
        } else {
          let newValue;
          switch (newCell.type) {
            case 'number':
              newValue = newCell.value;
              break;
            case 'date': {
              if (newCell.date) {
                const year = newCell.date.getFullYear();
                const month = String(newCell.date.getMonth() + 1).padStart(
                  2,
                  '0',
                );
                const day = String(newCell.date.getDate()).padStart(2, '0');
                newValue = `${year}-${month}-${day}`;
              } else {
                newValue = null;
              }
              break;
            }
            default:
              newValue = newCell.text;
          }

          newData[rowIndex] = {
            ...newData[rowIndex],
            [columnId]: newValue,
          };
        }
      });

      return newData;
    });
  }, []);

  // 행 추가
  const addRow = useCallback(newRow => {
    const newRowId = `new-${Date.now()}`;
    const newRowData = {
      id: newRowId,
      ...newRow,
    };

    setData(prevData => [...prevData, newRowData]);
    setAddedRows(prev => [...prev, newRowId]);

    return newRowId;
  }, []);

  // 추가된 행 여부
  const isNewRow = useCallback(
    rowId => {
      return (
        addedRows.includes(rowId) ||
        String(rowId).startsWith('new-') ||
        !originalIds.current.has(rowId)
      );
    },
    [addedRows],
  );

  // 선택된 행
  const getSelectedRows = useCallback(() => {
    return data.filter(row => row.is_selected);
  }, [data]);

  // 모든 새 행
  const getNewRows = useCallback(() => {
    return data.filter(row => isNewRow(row.id));
  }, [data, isNewRow]);

  const deleteSelectedRows = useCallback(() => {
    // 추가된 행 중 선택된 행 ids
    const selectedRowIds = data
      .filter(row => row.is_selected && isNewRow(row.id))
      .map(row => row.id);

    setData(prevData =>
      prevData.filter(row => !(row.is_selected && isNewRow(row.id))),
    );

    setAddedRows(prev => prev.filter(id => !selectedRowIds.includes(id)));

    return selectedRowIds;
  }, [data, isNewRow]);

  return {
    data,
    setData,
    resetData,
    handleCellsChanged,
    addRow,
    isNewRow,
    getNewRows,
    getSelectedRows,
    deleteSelectedRows,
    addedRows,
  };
}

export default useExcelData;
